import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CurrentUserService } from '@core/services/current-user.service';
import { environment } from '@environments/environment';
import { CurrentUser } from '@shared/interfaces';
import mixpanel from 'mixpanel-browser';

const enableAnalytics = environment.analytics.enable;

@Injectable({
    providedIn: 'root',
})
export class MixpanelService {
    private currentUser: CurrentUser | null = null;

    constructor(
        private currentUserService: CurrentUserService,
        private router: Router
    ) {
        this.currentUserService.currentUser$.subscribe(user => {
            this.currentUser = user;
            this.setMixpanelProfile();
        });

        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                let path = this.buildRouteConfigPath(router);
                this.logUrlNavigation(path);
            }
        });
    }

    logOpenModal(modalName: string, data: any = {}) {
        data.event_type = 'open-modal';
        this.logEvent('open-modal: ' + modalName, data);
    }

    logFormSubmit(formName: string) {
        this.logEvent('submit-form: ' + formName, {
            form_name: formName,
            event_type: 'submit-form',
        });
    }

    logEvent(eventName: string, data: any = {}) {
        if (!enableAnalytics) {
            return;
        }
        mixpanel.track(eventName, this.dataWithCustomProperties(data));
    }

    logHelpAndSupportClick(eventName: string, pageName: string) {
        this.logEvent('Help & Support - ' + eventName + ' - ' + pageName);
    }

    init() {
        if (!enableAnalytics) {
            return;
        }
        mixpanel.init(environment.analytics.mixpanelCode, { debug: true });
    }

    private logUrlNavigation(path: string) {
        this.logEvent('page-load: ' + path, {
            navigation_path: path,
            event_type: 'page-load',
        });
    }

    private setMixpanelProfile() {
        if (!enableAnalytics || !this.currentUser) {
            return;
        }
        mixpanel.identify(String(this.currentUser.id));
        mixpanel.people.set({
            username: this.currentUser.firstName + ' ' + this.currentUser.lastName,
            $email: this.currentUser.email,
        });
    }

    private buildRouteConfigPath(router: Router): string {
        let node = router?.routerState?.snapshot?.root;
        let pathSegments = <string[]>[];
        while (node?.children?.length > 0) {
            let childNode = node.children[0];
            if (childNode?.routeConfig?.path) {
                pathSegments.push(childNode.routeConfig.path);
            }
            node = childNode;
        }
        return '/' + pathSegments.join('/');
    }

    private dataWithCustomProperties(data: any): any {
        return {
            ...data,
            path_name: window.location.pathname,
            full_url: window.location.href,
            host_name: window.location.hostname,
        };
    }
}
